import _, { get } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// import businessLiteConfig from '../../../../BusinessLite.json';
import { observer } from 'mobx-react-lite';
import Logo from "../../../../assets/images/LogoNew.svg";
import { ApplicantHeaderActions, createApplicationFormUpdateHandler } from '../../../../components/ApplicationInvite';
import AutoSaveIndicator from '../../../../components/AutoSaveIndicator/AutoSaveIndicator';
import CountrySelectService from '../../../../components/CSC/CountrySelect/CountrySelectService';
import Stepper from '../../../../components/Common/Tab/Stepper';
import { FORM_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import ReviewShellBoundary from '../../../../components/ReviewWizard/ReviewShellBoundary';
import { getAccountActivityRuleErrors, shouldShowField } from '../../../../components/InputField/helper';
import LogoutWithReason from '../../../../components/LogoutWithReason/LogoutWithReason';
import useAutoSave from '../../../../hooks/useAutoSave';
import useFormReviewShell from '../../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import { DialogContext } from '../../../../store/context/DialogContext';
import { doGET, doPOST, doPUT } from '../../../../util/HttpUtil';
import { rememberApplicationLoginPath } from '../../../../util/authResume';
import { getOrdinalSuffix } from '../../../../util/Util';
import { handleUtmFormSubmit } from '../../../../util/utm';
import pincodeService from '../../components/PincodeField/PincodeService';
import { validatePincodeByCountry, withComputedHidePinCode } from '../../components/PincodeField/helper';
import { createBusinessLiteAutoSaveAdapter } from '../autosave/buildBusinessLiteAutoSavePayload';
import { buildPostAutoSaveUpdatePayload, createAutoSaveFieldDefinitionMap, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from '../autosave/shared';
import { fetchBPlusData, getBPlusStepNumberWhenRedo, STEP_NUM } from '../BPlushelper';
import ExtraQuestions from '../ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from '../askIdentity';
import { checkIsAskForInfo, convertArrayToDict, FORM_TYPE, scrollToTop, STEP_STATUS, STEPS, TRANSACTION_FIELDS } from '../IPlushelper';
import { AskMoreMessage, ThankYou } from '../components';
import ApplicationDocRedo from '../components/IndividualLite/ApplicationDocRedo';
import { addFieldsMapping, cleanupMappings, GroupedFields } from '../constants';
import { addFieldError, appendValidationContext, buildValidationFieldOrderForWizard, getFirstOrderedValidationMessage, normalizeValidationFieldPath } from '../helpers/validationOrder';
import { getIndividualShareholderOwnershipError } from '../helpers/ownershipValidation';
import { CONVERSIONS_FIELD_MAP } from '../IndividualPlus/constant';
import Agreements from './components/Agreements';
import ButtonGroupNew from './components/ButtonGroupNew';
import Page1 from './components/Page1';
import Page2 from './components/Page2';
import Page3 from './components/Page3';
import Page4 from './components/Page4';
import Page5 from './components/Page5';
import Page6 from './components/Page6';
import { compareAddresses } from './constants';
import { validateBusinessPlusField } from './validation';
import { getCarts } from '../../../../components/Payment/CartService';
import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from '../submitDocumentValidation';

const EXPECTED_ACTIVITY_FIELDS = [
    "Amount_Of_Initial_Deposit",
    ...TRANSACTION_FIELDS,
    "Expected_Amount_Monthly_Digital_Currency_Custody",
    "hasCoinConversionPlan",
    "hasCryptoConversionPlan",
];

// Business Plus still renders legacy numbered pages, but autosave routing/enablement is
// JSON-driven by semantic state names. This map is the only page-specific translation layer.
const AUTO_SAVE_STATE_BY_STEP = {
    [STEP_NUM.INTRODUCTION]: 'INTRODUCTION',
    [STEP_NUM.BUSINESS_DETAILS]: 'BUSINESS_DETAILS',
    [STEP_NUM.BUSINESS_ACTIVITY]: 'BUSINESS_ACTIVITY',
    [STEP_NUM.BUSINESS_COMPLIANCE]: 'BUSINESS_COMPLIANCE',
    [STEP_NUM.DOCUMENTS]: 'DOCUMENTS',
    [STEP_NUM.SELF_DECLARATION]: 'SELF_DECLARATION',
    [STEP_NUM.REFERRAL_AND_SOURCE]: 'REFERRAL_AND_SOURCE',
    [STEP_NUM.APPLICATION_FORM_REDO]: 'EXTRA_INFORMATION',
};

const REVIEW_WIZARD_TO_STEP = Object.entries(AUTO_SAVE_STATE_BY_STEP).reduce((acc, [stepValue, wizard]) => {
    acc[wizard] = Number(stepValue);
    return acc;
}, {});

const doesFieldBelongToStep = (fieldWizard, wizard) => {
    const stepWizard = AUTO_SAVE_STATE_BY_STEP[wizard];
    return fieldWizard === wizard || fieldWizard === stepWizard;
};

const buildLocalId = () =>
    `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const isBusinessPrimeApplication = (applicationForm = {}) => (
    applicationForm?.formType === FORM_TYPE.BusinessPrime
);

// New rows need a stable local identity until backend create returns a real _id/version.
const ensureDraftArrayItemIdentity = (items = [], identityFields = ['_id']) => (
    (Array.isArray(items) ? items : []).map((item) => {
        if (!_.isPlainObject(item)) {
            return item;
        }

        const hasStableIdentity = identityFields.some((field) => Boolean(item?.[field]));
        if (hasStableIdentity || item?._localId) {
            return item;
        }

        return {
            ...item,
            _isNew: true,
            _localId: buildLocalId(),
        };
    })
);

const doesMappingConditionMatch = (dataValue, conditionValue, conditionOperator = '===') => {
    switch (conditionOperator) {
        case '===':
            return dataValue === conditionValue;
        case '!==':
            return dataValue !== conditionValue;
        case '==':
            return dataValue == conditionValue;
        case '!=':
            return dataValue != conditionValue;
        case '>':
            return dataValue > conditionValue;
        case '<':
            return dataValue < conditionValue;
        default:
            return dataValue === conditionValue;
    }
};

const applyBusinessLiteFieldMappings = (sourceData) => {
    const updatedData = _.cloneDeep(sourceData || {});

    cleanupMappings.forEach(({ conditionPath, conditionValue, keys, conditionOperator = '===' }) => {
        const dataValue = _.get(updatedData, conditionPath);
        if (!doesMappingConditionMatch(dataValue, conditionValue, conditionOperator)) {
            return;
        }

        keys.forEach((key) => {
            const keyParts = key.split('.');
            let current = updatedData;

            for (let i = 0; i < keyParts.length - 1; i += 1) {
                if (current && current[keyParts[i]] !== undefined) {
                    current = current[keyParts[i]];
                } else {
                    current = undefined;
                    break;
                }
            }

            if (current && current[keyParts[keyParts.length - 1]] !== undefined) {
                delete current[keyParts[keyParts.length - 1]];
            }
        });
    });

    addFieldsMapping.forEach(({ conditionPath, conditionValue, addValues, conditionOperator = '===' }) => {
        const dataValue = _.get(updatedData, conditionPath);
        if (!doesMappingConditionMatch(dataValue, conditionValue, conditionOperator)) {
            return;
        }

        addValues.forEach(({ key, value }) => {
            const keyParts = key.split('.');
            let current = updatedData;

            for (let i = 0; i < keyParts.length - 1; i += 1) {
                if (!current[keyParts[i]]) {
                    current[keyParts[i]] = {};
                }
                current = current[keyParts[i]];
            }

            const leafKey = keyParts[keyParts.length - 1];
            if (current[leafKey] == undefined || current[leafKey] == null || !current[leafKey]?.length) {
                current[leafKey] = _.cloneDeep(value);
            }
        });
    });

    if (_.get(updatedData, 'hasEntity') == true && (!Array.isArray(updatedData.entities) || updatedData.entities.length === 0)) {
        updatedData.entities = [{}];
    }

    if (Array.isArray(updatedData.entities) && updatedData.entities.length > 0) {
        updatedData.entities = ensureDraftArrayItemIdentity(updatedData.entities, ['aEId', '_id']);
    }

    return updatedData;
};

const ApplyBusinessLite = ({ asLabel, editMode = false, formId, refreshId, onChange = () => { } }) => {
    const { edit_id } = useParams();
    let { showError, showConfirm } = useContext(DialogContext);
    const [data, setData] = useState({
        entities: [{}],
        individuals: [{ isPrimaryUser: true, }],
    });
    const [applicationForm, setApplicationForm] = useState({});
    const [isAskForInfo, setIsAskForInfo] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [editId, setEditID] = useState(edit_id ?? formId);
    const [applicationSteps, setApplicationSteps] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [step, setStep] = useState(STEP_NUM.INTRODUCTION);
    const [businessValidationFields, setbusinessValidationFields] = useState([])
    const [validationkeyValue, setvalidationkeyValue] = useState({});
    const [autoSaveConfig, setAutoSaveConfig] = useState(null);
    const [centralErrors, setCentralErrors] = useState({});
    const [reviewOverlayWizard, setReviewOverlayWizard] = useState("");
    const {
        reviewError,
        reviewLoading,
        reviewNavigation,
    } = useFormReviewShell({
        applicationId: editId,
        autoLoad: !asLabel && !editMode && Boolean(editId),
        fieldDefinitions: validationkeyValue,
        formData: data,
        currentWizard: reviewOverlayWizard
            || (step === STEP_NUM.APPLICATION_FORM_REDO ? "ExtraQuestions" : AUTO_SAVE_STATE_BY_STEP[step] || step),
        onPersistError: showError,
        onNavigateToWizard: (wizard) => {
            if (wizard === "ApplicationDocRedo") {
                setReviewOverlayWizard("ApplicationDocRedo");
                scrollToTop();
                return;
            }

            setReviewOverlayWizard("");

            if (wizard === "ExtraQuestions") {
                updateStep(STEP_NUM.APPLICATION_FORM_REDO);
                scrollToTop();
                return;
            }

            const nextStepValue = REVIEW_WIZARD_TO_STEP[wizard] ?? Number(wizard);
            if (Number.isFinite(nextStepValue)) {
                updateStep(nextStepValue);
                scrollToTop();
            }
        },
    });

    useEffect(() => {
        const qsStep = Number(new URLSearchParams(location.search).get('step'));
        if (!Number.isNaN(qsStep) && qsStep !== step && qsStep <= STEP_NUM.REFERRAL_AND_SOURCE) {
            setStep(qsStep);
        }
    }, [location.search]);

    const updateStep = useCallback(
        (nextStep) => {
            setReviewOverlayWizard("");
            setStep(nextStep);
            if (nextStep <= STEP_NUM.REFERRAL_AND_SOURCE) {
                const params = new URLSearchParams(location.search);
                params.set('step', String(nextStep));
                navigate({ search: params.toString() }, { replace: true });
            }
        },
        [location.search, navigate]
    );

    const decrementStep = () => {
        updateStep(prev => prev - 1);
    }

    useEffect(() => {
        onChange(removeUnnecessaryThingsBeforeUpdate(data))
    }, [data]);

    function handleChange(path, value, isCheckbox = false) {
        setData(prevData => {
            let updatedData = _.cloneDeep(prevData);

            // Update data based on checkbox or regular value
            if (isCheckbox) {
                if (_.get(updatedData, path)) {
                    _.set(updatedData, path, _.without(_.get(updatedData, path), value));
                } else {
                    let updatedArray = _.get(updatedData, path) || [];
                    updatedArray.push(value);
                    _.set(updatedData, path, updatedArray);
                }
            } else {
                _.set(updatedData, path, value);
            }

            return applyBusinessLiteFieldMappings(updatedData);
        });
    }

    function handleChangeViaObject(updateObject) {
        setData(prevData => {
            let updatedData = _.cloneDeep(prevData);

            Object.keys(updateObject).forEach(key => {
                _.set(updatedData, key, updateObject[key]);
            });

            return applyBusinessLiteFieldMappings(updatedData);
        });
    }

    useEffect(() => {
        if (formId) {
            setEditID(formId)
        }
    }, [formId])

    useEffect(() => {
        if (editId && data?.interestedInPrime) {
            rememberApplicationLoginPath({
                applicationId: editId,
                loginPath: "/business-prime-plus/login",
            });
        }
    }, [data?.interestedInPrime, editId]);

    const fetchCountryCodes = async () => {
        try {
            await CountrySelectService.fetch();
        } catch (error) {
            showError(error);
        }
    };

    const getValidationJSON = async () => {
        try {
            await fetchCountryCodes();
            const response = await doGET("/api/cyclos/fields-json?group=Businesses_Lite");
            if (response.status === 200) {
                // const bJSON = businessLiteConfig;
                const bJSON = response?.data ?? {};
                setAutoSaveConfig(bJSON?.autoSave || null);
                setbusinessValidationFields(bJSON?.fields)
                const fieldsMap = createAutoSaveFieldDefinitionMap(bJSON?.fields);

                setvalidationkeyValue(fieldsMap)
            }
        } catch (error) {
            showError(error);
        }
    };

    useEffect(() => {
        getValidationJSON()
        CountrySelectService.fetchCyclosCountryMaster();
    }, []);

    const movePrimaryUserToFront = (list = []) => {
        if (!Array.isArray(list)) return [];

        try {
            const idx = list.findIndex(item => item?.isPrimaryUser);
            if (idx <= 0) return list;
            return [
                list[idx],
                ...list.slice(0, idx),
                ...list.slice(idx + 1)
            ];
        } catch (err) {
            console.error('movePrimaryUserToFront failed:', err);
            return Array.isArray(list) ? [...list] : [];
        }
    };

    const finalizeBusinessLiteResponse = (businessLiteResponse = {}) => {
        const nextResponse = {
            ...(businessLiteResponse ?? {}),
        };

        nextResponse.individuals = movePrimaryUserToFront(nextResponse?.individuals);

        if (!nextResponse?.individuals?.length) {
            // individuals array is empty or doesn't exist
            nextResponse.individuals = [{
                email: nextResponse.email,
                accessType: "NoGroup",
                isPrimaryUser: true
            }];
        }
        nextResponse.individuals = normalizeBusinessLiteIndividuals(
            nextResponse.individuals,
            nextResponse.email
        );

        if (!nextResponse.address) {
            nextResponse.address = {};
        }

        // Ensure addressList is an array
        if (!Array.isArray(nextResponse.addressList)) {
            nextResponse.addressList = [];
        }

        const principalAddress = nextResponse.address;
        const addressList = nextResponse.addressList;

        // For REGISTERED address at index 0:
        const registeredIndex = addressList.findIndex(addr => addr.type === "REGISTERED");
        if (registeredIndex === -1) {
            // Not present, add it at index 0 using principal address data
            addressList.splice(0, 0, { type: "REGISTERED" });
        } else if (registeredIndex !== 0) {
            // Exists but not at index 0, move it to index 0
            const registeredAddress = addressList.splice(registeredIndex, 1)[0];
            addressList.splice(0, 0, registeredAddress);
        }

        // For MAILING address at index 1:
        const mailingIndex = addressList.findIndex(addr => addr.type === "MAILING");
        if (mailingIndex === -1) {
            // Not present, add it at index 1 using principal address data
            addressList.splice(1, 0, { type: "MAILING" });
        } else if (mailingIndex !== 1) {
            // Exists but not at index 1, move it to index 1
            const mailingAddress = addressList.splice(mailingIndex, 1)[0];
            addressList.splice(1, 0, mailingAddress);
        }

        // ----- Compare Addresses to Set Flags -----

        const registeredAddress = addressList.find(addr => addr.type === "REGISTERED") || {};
        const mailingAddress = addressList.find(addr => addr.type === "MAILING") || {};
        const isRegisteredSame = compareAddresses(principalAddress, registeredAddress);
        const isMailingSame = compareAddresses(principalAddress, mailingAddress);
        nextResponse.isRegisteredAddressSame = isRegisteredSame;
        nextResponse.isMailingAddressSame = isMailingSame;
        nextResponse.addressList = addressList;

        // hide/show pincode fields based on country (self-heals a missing pinCode when the
        // country has a fixed default, e.g. HK -> 999077)
        nextResponse.address = withComputedHidePinCode(nextResponse.address);

        nextResponse.addressList = addressList.map(addr => withComputedHidePinCode(addr));

        nextResponse.individuals = ensureDraftArrayItemIdentity(
            nextResponse.individuals.map(ind => ({
                ...(ind ?? {}),
                address: withComputedHidePinCode(ind.address)
            })),
            ['_id', 'aEId', 'cyclosUserId', 'cyclosUserName']
        );
        if (nextResponse?.hasEntity && (!Array.isArray(nextResponse.entities) || nextResponse.entities.length === 0)) {
            nextResponse.entities = [{}];
        }
        nextResponse.entities = ensureDraftArrayItemIdentity(
            nextResponse.entities || [],
            ['aEId', '_id']
        );
        nextResponse.addressList = ensureDraftArrayItemIdentity(
            nextResponse.addressList,
            ['type']
        );

        return nextResponse;
    };

    const fetchDetail = async (stepProp, { syncUi = true } = {}) => {
        let didRedirectToPrime = false;
        if (syncUi) {
            setDataLoading(true)
        }
        try {
            if (!editId) {
                return null;
            }

            localStorage.setItem("formId", editId);
            if (Object.keys(pincodeService?.records)?.length === 0) {
                pincodeService.fetch().catch(console.error);
            }

            const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`);
            const applicationFormData = applicationResponse?.status == 200
                ? (applicationResponse?.data ?? {})
                : {};

            if (applicationResponse?.status == 200 && isBusinessPrimeApplication(applicationFormData)) {
                didRedirectToPrime = true;
                navigate(`/business-prime/${editId}`, { replace: true });
                return null;
            }

            const businessLiteResponse = await fetchBPlusData(editId);

            if (applicationResponse?.status == 200 && syncUi && applicationFormData?.steps?.length) {
                const askInfoStep = checkIsAskForInfo(applicationFormData?.steps);
                const stepsObj = convertArrayToDict(applicationFormData?.steps);
                setApplicationSteps(stepsObj);

                if (stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status > 0
                    && [1, 3, 4]?.includes(stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status)
                    && stepsObj[STEPS.APPLICATION_FORM]?.status > 0
                    && [1, 3, 4]?.includes(stepsObj[STEPS.APPLICATION_FORM]?.status)
                ) {
                    updateStep(STEP_NUM.THANK_YOU);
                }

                if (stepsObj[STEPS.APPLICATION_FORM]?.status > 0 && Object.keys(askInfoStep ?? {}).length && [STEPS.APPLICATION_FORM, STEPS.DOCUMENT_VERIFICATION]?.includes(askInfoStep?.label)) {
                    const askInfoStepData = { ...(askInfoStep ?? {}), isAskForInfo: true, step: getBPlusStepNumberWhenRedo(askInfoStep?.label), };
                    setIsAskForInfo(askInfoStepData);

                    if (askInfoStep?.label === STEPS.APPLICATION_FORM && hasPendingAsks(businessLiteResponse?.asks)) {
                        updateStep(STEP_NUM.APPLICATION_FORM_REDO);
                    } else {
                        updateStep(STEP_NUM.THANK_YOU);
                    }
                } else {
                    setIsAskForInfo(null);
                }
            }

            if (applicationResponse?.status == 200 && syncUi) {
                setApplicationForm(applicationFormData);
            }

            const formData = finalizeBusinessLiteResponse(businessLiteResponse);
            if (syncUi) {
                setData(formData);
            }
            return formData;
        } catch (error) {
            console.log(error)
        } finally {
            if (syncUi) {
                setDataLoading(false)
                if (!didRedirectToPrime) {
                    if (stepProp) updateStep(stepProp)
                    scrollToTop()
                }
            }
        }
        return null;
    }

    const isAutoSaveEnabled = !asLabel
        && !!editId
        && !!data?._id
        && !loading
        && !dataLoading
        && isAutoSaveEnabledForConfig({
            autoSaveConfig,
            step: AUTO_SAVE_STATE_BY_STEP[step] || null,
        });
    const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

    const handleDirtyUpdate = useCallback(async () => {
        return await fetchDetail(undefined, { syncUi: false });
    }, [fetchDetail]);

    const autoSaveAdapter = useMemo(
        () => createBusinessLiteAutoSaveAdapter(validationkeyValue, autoSaveConfig),
        [autoSaveConfig, validationkeyValue]
    );

    const {
        autoSaveStatus,
        lastSavedAt,
        pendingChangesCount,
        pendingChanges,
        hasUnsavedChanges,
        isAutoSaveNavigationPending,
        flushAndPauseAutoSave,
        resumeAutoSave,
    } = useAutoSave({
        data,
        isEnabled: isAutoSaveEnabled,
        debounceMs: autoSaveDebounceMs,
        adapter: autoSaveAdapter,
        onDirtyUpdate: handleDirtyUpdate,
        onDataReconcile: setData,
        onFieldPersisted: reviewNavigation?.commitFieldTask,
    });

    useUnsavedChangesWarning({
        when: hasUnsavedChanges || loading,
        isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
        pendingChangesCount,
    });

    const nextActionLoading = loading || isAutoSaveNavigationPending;
    const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];

    useEffect(() => {
        fetchDetail();
    }, [editId, refreshId]);

    const normalizeBusinessLiteIndividual = (individual, index, rootEmail) => {
        if (!_.isPlainObject(individual)) {
            return individual;
        }

        const nextIndividual = { ...(individual ?? {}) };
        nextIndividual.isControlPerson = false;

        if (index === 0) {
            nextIndividual.isPrimaryUser = true;
            nextIndividual.isAuthorizedUser = true;
            if (!nextIndividual.email && rootEmail) {
                nextIndividual.email = rootEmail;
            }
            if (!nextIndividual.accessType) {
                nextIndividual.accessType = "NoGroup";
            }
        } else if (nextIndividual.isAuthorizedUser && !nextIndividual.accessType) {
            nextIndividual.accessType = "NoGroup";
        }

        return nextIndividual;
    };

    const normalizeBusinessLiteIndividuals = (individuals = [], rootEmail = data?.email) => (
        (Array.isArray(individuals) ? individuals : []).map((individual, index) =>
            normalizeBusinessLiteIndividual(individual, index, rootEmail)
        )
    );

    const removeUnnecessaryThingsBeforeUpdate = (payload) => {
        let payloadToPass = payload ?? {};

        if (payloadToPass?.individuals?.length) {
            payloadToPass.individuals = normalizeBusinessLiteIndividuals(payloadToPass.individuals, payloadToPass.email);
        }

        if (payloadToPass?.interestedInPrime) {
            payloadToPass.talkToSales = true;
        }

        if (!payloadToPass?.hasEntity) {
            payloadToPass.entities = [];
        }

        // Initialize an array to hold keys that need to be omitted from the returned payload
        const keysToOmit = [];

        // Conditionally add keys to omit based on the presence of landlinePhone and mobilePhone
        if (!payloadToPass?.landlinePhone) {
            keysToOmit.push('landlineCountryCode');
        }

        if (!payloadToPass?.mobilePhone) {
            keysToOmit.push('mobileCountryCode');
        }

        // Use lodash to omit empty strings and the specified keys
        const cleanedPayload = _.omit(
            _.omitBy(payloadToPass, value => value === ""),
            keysToOmit
        );

        return cleanedPayload;
    };

    const handleUpdate = async ({
        skipUpdate,
        nextStep,
        payload,
        isfetchDetail,
        isLastStep = false
    }) => {
        let errorFlag = false;
        let latestData = data;
        let didResumeAutoSave = false;
        if ((payload ?? data)?._id) {
            setLoading(true)
            setHasError(false);
            const autoSaveResult = await flushAndPauseAutoSave();
            if (!autoSaveResult?.ok) {
                setLoading(false);
                setHasError(true);
                showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
                return null;
            }
            latestData = autoSaveResult?.latestData || data;
            const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.()
                || currentPageReviewTasks;
            const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.()
                || reviewNavigation?.actionableTasks
                || [];
            if (nextStep && !isLastStep && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
                const blockedCurrentPageTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "")
                    || refreshedCurrentPageReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please finish the remaining items on this page before continuing.");
                if (blockedCurrentPageTask) {
                    reviewNavigation?.goToTask(blockedCurrentPageTask);
                }
                return { status: 400 };
            }
            if (isLastStep && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
                const blockedActionableTask = reviewNavigation?.peekPreferredActionableTask?.()
                    || refreshedActionableReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please complete all requested items before submitting.");
                if (blockedActionableTask) {
                    reviewNavigation?.goToTask(blockedActionableTask);
                }
                return { status: 400 };
            }
            let submissionData = latestData;
            try {
                if (!asLabel && !skipUpdate) {
                    const updatePayload = removeUnnecessaryThingsBeforeUpdate(
                        buildPostAutoSaveUpdatePayload(latestData, payload)
                    );
                    const response = await doPUT(`/api/business-lite/update`, updatePayload)
                    if (response?.status == 200) {

                        if (nextStep === STEP_NUM.BUSINESS_ACTIVITY) {
                            const applicationFormData = await doGET(`/api/application-form/detail?id=${editId}`)
                            if (applicationFormData?.status == 200) {
                                const updatedpplicationFormData = await doPUT(`/api/application-form/update`, {
                                    ...applicationFormData?.data,
                                })
                                if (updatedpplicationFormData?.status == 200) {
                                    const mergedData = {
                                        ...response?.data,
                                    };
                                    setData(mergedData);
                                    submissionData = mergedData;
                                    resumeAutoSave(mergedData);
                                    didResumeAutoSave = true;
                                }
                                else {
                                    const nextData = {
                                        ...latestData,
                                        ...response?.data,
                                    };
                                    setData(nextData);
                                    submissionData = nextData;
                                    resumeAutoSave(nextData);
                                    didResumeAutoSave = true;
                                }
                            }
                            else {
                                const nextData = {
                                    ...latestData,
                                    ...response?.data,
                                };
                                setData(nextData);
                                submissionData = nextData;
                                resumeAutoSave(nextData);
                                didResumeAutoSave = true;
                            }
                        } else {
                            const mergedData = response?.data;
                            const nextData = {
                                ...latestData,
                                ...mergedData,
                            };
                            setData(nextData)
                            submissionData = nextData;
                            resumeAutoSave(nextData);
                            didResumeAutoSave = true;
                        }
                    } else {
                        errorFlag = true
                        resumeAutoSave(null);
                        didResumeAutoSave = true;
                    }
                }


                if (isLastStep && !asLabel) {
                    const documentValidation = await validateSubmitDocuments({
                        flow: SUBMIT_DOC_FLOW.BUSINESS_PLUS_ONBOARDING,
                        recordId: editId,
                        data: submissionData,
                        applicationForm,
                    });

                    if (!documentValidation?.isValid) {
                        setHasError(true);
                        showError(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
                        errorFlag = true;
                        return { status: 400 };
                    }


                    const submitResponse = await doGET(`/api/business-lite/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`)
                    // try {
                    //     doGET(`/api/application-doc/cert/beneficial-owners?form_id=${editId}`)
                    // } catch (error) {

                    // }
                    if (submitResponse?.status == 200) {
                        await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
                        handleUtmFormSubmit({
                            formType: "BUSINESS_PLUS",
                            utmPayload: {
                                utm_source: applicationForm?.extension?.utm_source,
                                utm_medium: applicationForm?.extension?.utm_medium,
                                utm_campaign: applicationForm?.extension?.utm_campaign,
                                utm_content: applicationForm?.extension?.utm_content,
                                utm_term: applicationForm?.extension?.utm_term,
                            }
                        });
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        if (isfetchDetail && nextStep) {
                            await fetchDetail(nextStep);
                            setLoading(false);
                        }
                    } else {
                        errorFlag = true
                    }
                } else {
                    if (isfetchDetail && nextStep) fetchDetail(nextStep)
                    else if (nextStep) updateStep(nextStep)
                    scrollToTop()
                    setLoading(false);
                }
                setLoading(false);
            } catch (error) {
                console.error('There has been a problem with your fetch operation:', error);
                setHasError(true);
                showError(error)
                errorFlag = true
                resumeAutoSave(null);
                didResumeAutoSave = true;
            }
            finally {
                if (skipUpdate && !didResumeAutoSave) {
                    resumeAutoSave(null);
                }
                setLoading(false)
            }
        } else {
            if (nextStep) fetchDetail(nextStep)
            resumeAutoSave(null);
        }

        return errorFlag ? { status: 400 } : { status: 200 }

    };

    const askForConversionValueUpdate = async (payloadToCheck) => {
        const fieldsToUpdate = [];
        if (
            payloadToCheck?.hasCoinConversionPlan === false && (
                payloadToCheck?.Number_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                payloadToCheck?.Amount_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                payloadToCheck?.Number_Of_Monthly_Stablecoin_Conversions_Payments > 0 ||
                payloadToCheck?.Amount_Of_Monthly_Stablecoin_Conversions_Payments > 0
            )) {
            fieldsToUpdate.push("hasCoinConversionPlan");
        }
        if (
            payloadToCheck?.hasCryptoConversionPlan === false && (
                payloadToCheck?.Count_Of_Monthly_Cryptocurrency_Conversions > 0 || payloadToCheck?.Amount_Of_Monthly_Cryptocurrency_Conversions > 0
            )) {
            fieldsToUpdate.push("hasCryptoConversionPlan");
        }
        if (fieldsToUpdate.length === 0) {
            return { success: true, fieldsToUpdate: [], error: "" };
        } else {
            const title = fieldsToUpdate.map(field => CONVERSIONS_FIELD_MAP?.[field] ?? field).join(" and ");
            const response = await showConfirm({
                title: `Do you want to update ${title} to Yes?`,
            });
            if (response) return { success: true, fieldsToUpdate, error: '' };
            else return { success: false, fieldsToUpdate, error: `You have to update ${title} to Yes or change their values to 0 to proceed.` };
        }
    }

    const handleNext = (wizard) => {
        const fieldErrors = new Map();
        const generalErrors = [];
        const orderedValidationFields = [];
        const seenOrderedValidationFields = new Set();
        const trackValidationField = (field) => {
            if (!field) {
                return;
            }
            const normalizedField = normalizeValidationFieldPath(field);
            if (!seenOrderedValidationFields.has(normalizedField)) {
                seenOrderedValidationFields.add(normalizedField);
                orderedValidationFields.push(normalizedField);
            }
        };
        const requiredFields = businessValidationFields
            .filter(({ wizard: fieldWizard, required, showIf }) =>
                doesFieldBelongToStep(fieldWizard, wizard) &&
                (required || showIf?.required) &&
                shouldShowField(data, showIf)
            )
            .map(({ field, error }) => ({ field, error }));



        const fieldsToValidate = wizard === STEP_NUM.BUSINESS_DETAILS
            ? requiredFields?.filter(({ field }) =>
                !GroupedFields?.includes(field)
            )
            : requiredFields;
        const shouldSkipExpectedActivity = wizard === STEP_NUM.BUSINESS_ACTIVITY && data?.interestedInPrime;
        const resolvedFieldsToValidate = shouldSkipExpectedActivity
            ? fieldsToValidate.filter(({ field }) => !EXPECTED_ACTIVITY_FIELDS.includes(field))
            : fieldsToValidate;

        // Find the index of "mobilePhone" in fieldsToValidate
        const mobilePhoneIndex = fieldsToValidate.findIndex(({ field }) => field === 'mobilePhone');



        Object.entries(centralErrors || {}).forEach(([field, value]) => {
            const message = typeof value === "string"
                ? value
                : (value?.hasError ? value?.errorMsg : "");
            trackValidationField(field);
            addFieldError(fieldErrors, field, message);
        })

        resolvedFieldsToValidate?.forEach(({ field, error }) => {
            const fieldValue = field?.includes('.') ? _.get(data, field) : data[field];
            trackValidationField(field);
            if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                addFieldError(fieldErrors, field, error || `The field ${field} is required.`);
            }
            if (field == "address.pinCode") {
                const countryValue = _.get(data, "address.country");
                if (countryValue) {
                    const isValidPincode = validatePincodeByCountry(countryValue, fieldValue);
                    if (!isValidPincode) {
                        addFieldError(fieldErrors, field, "Please enter a valid postal/zip code.");
                    }
                }
                return;
            }

            const fieldError = handleValidation(field, fieldValue);
            if (fieldError) {
                addFieldError(fieldErrors, field, fieldError);
            }
        });

        if (wizard === STEP_NUM.BUSINESS_DETAILS) {
            if (data?.individuals) {
                let mobileSet = new Set();
                let emailSet = new Set();
                const normalizedIndividuals = normalizeBusinessLiteIndividuals(data?.individuals, data?.email);

                const maxAuthorizedUsers = 11;
                const authorizedUsers = normalizedIndividuals.filter(ind => ind?.isAuthorizedUser) || [];

                if (authorizedUsers.length > maxAuthorizedUsers) {
                    generalErrors.push(`Only ${maxAuthorizedUsers} authorized users are allowed. Currently, there are ${authorizedUsers.length}.`);
                }
                const individualOwnershipError = getIndividualShareholderOwnershipError(normalizedIndividuals);
                if (individualOwnershipError) {
                    generalErrors.push(individualOwnershipError);
                }

                const controllingIndividuals = normalizedIndividuals.filter(ind => ind?.haveControlOverEntity) || [];
                if (!controllingIndividuals?.length) {
                    addFieldError(fieldErrors, 'individuals[].haveControlOverEntity', `Atleast one person should have control over the entity.`);
                }
                if (controllingIndividuals?.length > 1) {
                    addFieldError(fieldErrors, 'individuals[].haveControlOverEntity', `Exactly one person should have control over the entity.`);
                }

                normalizedIndividuals.forEach((user, idx) => {
                    const ordinalIdx = getOrdinalSuffix(idx + 1);
                    const isShareHolder = user?.isShareHolder === true;
                    const isAuthorizedUser = user?.isAuthorizedUser === true;
                    const isIndependentDirector = user?.isIndependentDirector === true;
                    const isDirector = user?.isDirector === true;
                    const requiresIndividualDetails = isShareHolder || isAuthorizedUser || isIndependentDirector;

                    let validations = requiresIndividualDetails ? [
                        { field: 'individuals[].firstName', message: handleValidation('individuals[].firstName', user?.firstName) },
                        { field: 'individuals[].middleName', message: handleValidation('individuals[].middleName', user?.middleName) },
                        { field: 'individuals[].lastName', message: handleValidation('individuals[].lastName', user?.lastName) },
                        { field: 'individuals[].address.street', message: handleValidation('individuals[].address.street', user?.address?.street) },
                        { field: 'individuals[].address.buildingNumber', message: handleValidation('individuals[].address.buildingNumber', user?.address?.buildingNumber) },
                        { field: 'individuals[].address.city', message: handleValidation('individuals[].address.city', user?.address?.city) },
                        { field: 'individuals[].address.state', message: handleValidation('individuals[].address.state', user?.address?.state) },
                        { field: 'individuals[].address.pinCode', message: user?.address?.hidePinCode || validatePincodeByCountry(user?.address?.country, user?.address?.pinCode) ? null : "Please enter a valid postal/zip code." },
                        { field: 'individuals[].address.country', message: handleValidation('individuals[].address.country', user?.address?.country) },
                        { field: 'individuals[].dob', message: handleValidation('individuals[].dob', user?.dob) },
                        { field: 'individuals[].nationality', message: handleValidation('individuals[].nationality', user?.nationality) },
                        { field: 'individuals[].email', message: handleValidation('individuals[].email', user?.email) },
                        { field: 'individuals[].mobilePhone', message: handleValidation('individuals[].mobilePhone', user?.mobilePhone) },
                        { field: 'individuals[].taxId', message: handleValidation('individuals[].taxId', user?.taxId) },
                        { field: 'individuals[].countryOfIssuance', message: handleValidation('individuals[].countryOfIssuance', user?.countryOfIssuance) }
                    ] : [];
                    if (isAuthorizedUser) {
                        validations = [
                            ...validations,
                            { field: 'individuals[].role', message: handleValidation('individuals[].role', user?.role) },
                            { field: 'individuals[].Mother_Maiden_Name', message: handleValidation('individuals[].Mother_Maiden_Name', user?.Mother_Maiden_Name) },
                            { field: 'individuals[].accessType', message: handleValidation('individuals[].accessType', user?.accessType) },
                        ]
                    }

                    if (requiresIndividualDetails && (["USA", "US"]?.includes(user?.address?.country) || ["USA", "US"]?.includes(user?.nationality))) {
                        validations = [
                            ...validations,
                            { field: 'individuals[].ssn', message: handleValidation('individuals[].ssn', user?.ssn) },
                        ]
                    }

                    if (isShareHolder) {
                        validations = [
                            ...validations,
                            { field: 'individuals[].percentOwner', message: handleValidation('individuals[].percentOwner', user?.percentOwner) },
                        ];

                        const percentValue = parseFloat(user?.percentOwner);
                        if (!isNaN(percentValue)) {
                            if (percentValue <= 0) {
                                generalErrors.push(`Percent ownership must be greater than 0% for ${ordinalIdx} user.`);
                            } else if (percentValue > 100) {
                                generalErrors.push(`Percent ownership cannot exceed 100% for ${ordinalIdx} user.`);
                            }
                        }
                    }

                    validations.forEach(({ field, message }) => {
                        trackValidationField(field);
                        if (message) addFieldError(fieldErrors, field, appendValidationContext(message, `${ordinalIdx} officer`));
                    });


                    // Check unique mobileCountryCode + mobilePhone combination
                    if (user?.mobileCountryCode && user?.mobilePhone) {
                        const mobileCombination = `${user.mobileCountryCode}-${user.mobilePhone}`;
                        if (mobileSet.has(mobileCombination)) {
                            generalErrors.push(`Duplicate mobile phone combination: ${user.mobileCountryCode}-${user.mobilePhone} for ${ordinalIdx} user.`);
                        } else {
                            mobileSet.add(mobileCombination);
                        }
                    }

                    if (user?.email) {
                        if (emailSet.has(user.email)) {
                            generalErrors.push(`Duplicate email: ${user.email} for ${ordinalIdx} user.`);
                        } else {
                            emailSet.add(user.email);
                        }
                    }

                    if (!isAuthorizedUser && !isShareHolder && !isIndependentDirector && !isDirector) {
                        trackValidationField('individuals[].isShareHolder');
                        addFieldError(fieldErrors, 'individuals[].isShareHolder', `User ${ordinalIdx} should be either shareholder, authorized user, or director.`);
                    }

                    let singleSelectionFields = [
                        { field: 'isShareHolder', validationKey: 'individuals[].isShareHolder' },
                        { field: 'isAuthorizedUser', validationKey: 'individuals[].isAuthorizedUser' },
                    ];

                    if (!isAuthorizedUser && !isShareHolder) {
                        singleSelectionFields = [
                            ...singleSelectionFields,
                            { field: 'isIndependentDirector', validationKey: 'individuals[].isIndependentDirector' },
                        ]
                    }

                    if (isAuthorizedUser || isShareHolder) {
                        singleSelectionFields = [
                            ...singleSelectionFields,
                            { field: 'haveControlOverEntity', validationKey: 'individuals[].haveControlOverEntity' },
                            { field: 'isDirector', validationKey: 'individuals[].isDirector' },
                        ]
                    }

                    if (isAuthorizedUser) {
                        singleSelectionFields = [
                            ...singleSelectionFields,
                            { field: 'isAuthorizedContact', validationKey: 'individuals[].isAuthorizedContact' },
                            { field: 'isAgentWithAttorneyPower', validationKey: 'individuals[].isAgentWithAttorneyPower' }
                        ]
                    }

                    singleSelectionFields.forEach(({ field, validationKey }) => {
                        trackValidationField(validationKey);
                        const validation = handleValidation(validationKey, user?.[field]);
                        if (validation) addFieldError(fieldErrors, validationKey, appendValidationContext(validation, `${ordinalIdx} user`));
                    });

                    if (user?.isShareHolder) {
                        trackValidationField('individuals[].sourceOfWealthList');
                        const validation = handleValidation('individuals[].sourceOfWealthList', user?.sourceOfWealthList);
                        if (validation) {
                            addFieldError(fieldErrors, 'individuals[].sourceOfWealthList', appendValidationContext(validation, `${ordinalIdx} officer`));
                        }
                    }


                });

                var addressTypeSet = new Set();

                data?.addressList?.forEach((address, idx) => {
                    const ordinalIdx = getOrdinalSuffix(idx + 1);
                    let validations = [
                        { field: 'addressList[].type', message: handleValidation('addressList[].type', address?.type) },
                        { field: 'addressList[].street', message: handleValidation('addressList[].street', address?.street) },
                        { field: 'addressList[].buildingNumber', message: handleValidation('addressList[].buildingNumber', address?.buildingNumber) },
                        { field: 'addressList[].city', message: handleValidation('addressList[].city', address?.city) },
                        { field: 'addressList[].state', message: handleValidation('addressList[].state', address?.state) },
                        { field: 'addressList[].pinCode', message: address?.hidePinCode || validatePincodeByCountry(address?.country, address?.pinCode) ? null : "Please enter a valid postal/zip code." },
                        { field: 'addressList[].country', message: handleValidation('addressList[].country', address?.country) },
                    ];
                    if (addressTypeSet.has(address.type)) {
                        generalErrors.push(`Duplicate address type: ${address.type} for ${ordinalIdx} address.`);
                    } else {
                        addressTypeSet.add(address.type);
                    }

                    validations.forEach(({ field, message }) => {
                        trackValidationField(field);
                        if (message) addFieldError(fieldErrors, field, appendValidationContext(message, `${_.lowerCase(address?.type)} address`));
                    });
                });

                if (data?.hasEntity) {
                    data?.entities?.forEach((entity, idx) => {
                        const ordinalIdx = getOrdinalSuffix(idx + 1);
                        let validations = [
                            { field: "entities[].relations", message: handleValidation("entities[].relations", entity?.relations) },
                            { field: "entities[].name", message: handleValidation("entities[].name", entity?.name) },
                            { field: "entities[].incorpCountry", message: handleValidation("entities[].incorpCountry", entity?.incorpCountry) },
                            { field: "entities[].website", message: handleValidation("entities[].website", entity?.website) },
                            { field: 'entities[].businessNature', message: handleValidation('entities[].businessNature', entity?.businessNature) },
                            { field: "entities[].yearOfIncorporation", message: handleValidation("entities[].yearOfIncorporation", entity?.yearOfIncorporation) },
                        ];
                        if (entity?.relations === "shareholder") {
                            validations = [
                                ...validations,
                                { field: "entities[].percentOwner", message: handleValidation("entities[].percentOwner", entity?.percentOwner) },
                                { field: "entities[].sourceOfWealthList", message: handleValidation("entities[].sourceOfWealthList", entity?.sourceOfWealthList) },
                            ];

                            const percentValue = parseFloat(entity?.percentOwner);
                            if (!isNaN(percentValue)) {
                                if (percentValue <= 0) {
                                    generalErrors.push(`Percent ownership must be greater than 0% for ${ordinalIdx} entity.`);
                                } else if (percentValue > 100) {
                                    generalErrors.push(`Percent ownership cannot exceed 100% for ${ordinalIdx} entity.`);
                                }
                            }
                        }

                        validations.forEach(({ field, message }) => {
                            trackValidationField(field);
                            if (message) addFieldError(fieldErrors, field, appendValidationContext(message, `${ordinalIdx} entity`));
                        });
                    });
                }
            }
        }

        if (!data?.tnc && step == STEP_NUM.REFERRAL_AND_SOURCE) {
            generalErrors.push("Please accept information provided in the Certificate of Beneficial Owners.");
        }

        if (!data?.acceptDelarationAggrement && step == STEP_NUM.SELF_DECLARATION) {
            generalErrors.push("Please accept important information about procedures for opening an account");
        }

        if (wizard === STEP_NUM.BUSINESS_ACTIVITY && !data?.interestedInPrime) {
            const crossFieldErrors = getAccountActivityRuleErrors(data, {
                depositLocationFields: ["Deposit_Locations"],
                paymentLocationFields: ["Payment_Locations"]
            });
            crossFieldErrors.forEach(({ field, message }) => addFieldError(fieldErrors, field, message));
        }

        const firstErrorMessage = getFirstOrderedValidationMessage({
            orderedFields: [
                ...orderedValidationFields,
                ...buildValidationFieldOrderForWizard({
                    fields: businessValidationFields,
                    wizard,
                    matchesWizard: doesFieldBelongToStep,
                }),
            ].filter((field, index, arr) => arr.indexOf(field) === index),
            fieldErrors,
            generalErrors,
        });

        if (firstErrorMessage) {
            setHasError(true);
            showError(firstErrorMessage);
            return false;
        }

        setHasError(false);
        return true;
    }

    const handleSubmit = async (v, isLastStep, isfetchDetail) => {
        if (handleNext(v - 1)) {
            const isBusinessActivityStep = (v - 1) === STEP_NUM.BUSINESS_ACTIVITY;
            if (isBusinessActivityStep && !data?.interestedInPrime) {
                const { success, fieldsToUpdate, error } = await askForConversionValueUpdate(data);
                if (!success) {
                    setHasError(true);
                    showError(error);
                    return;
                }

                const payloadToSubmit = fieldsToUpdate?.length
                    ? { ...data, ...Object.fromEntries(fieldsToUpdate.map(field => [field, true])) }
                    : data;

                if (fieldsToUpdate?.length) {
                    setData(prev => ({ ...prev, ...Object.fromEntries(fieldsToUpdate.map(field => [field, true])) }));
                }

                await handleUpdate({ nextStep: v, isLastStep, isfetchDetail, payload: payloadToSubmit });
                return;
            }
            await handleUpdate({ nextStep: v, isLastStep, isfetchDetail });
        }
    };

    const hasActionableReviewTasks = Boolean(reviewNavigation?.hasActionableTasks);
    const hasVisibleReviewTasks = Boolean(reviewNavigation?.hasVisibleTasks);
    const hasResolvedNoActionableReviewTasks = !reviewLoading && !hasActionableReviewTasks;
    const applicationFormStepStatus = applicationSteps[STEPS.APPLICATION_FORM]?.status;
    const documentVerificationStepStatus = applicationSteps[STEPS.DOCUMENT_VERIFICATION]?.status;
    const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
        && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);

    const shouldShowReviewWizard = !asLabel
        && !editMode
        && hasVisibleReviewTasks
        && step !== STEP_NUM.APPLICATION_FORM_REDO;

    const isDocumentRedoView = !asLabel
        && !editMode
        && (
            reviewOverlayWizard === "ApplicationDocRedo"
            || isDocumentRedoRequired
        );

    const isAskMoreMessageView = !asLabel
        && !editMode
        && hasResolvedNoActionableReviewTasks
        && step > STEP_NUM.APPLICATION_FORM_REDO
        && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
        && isAskForInfo?.label == STEPS.APPLICATION_FORM;

    const isExtraQuestionsView = !asLabel
        && !editMode
        && hasPendingAsks(data?.asks)
        && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
        && applicationFormStepStatus == STEP_STATUS.INCOMPLETE
        && isAskForInfo?.label == STEPS.APPLICATION_FORM;

    const isThankYouView = !asLabel
        && !editMode
        && (
            applicationSteps[STEPS.APPLICATION_FORM]?.status == STEP_STATUS.SUBMITTED
            || (
                hasResolvedNoActionableReviewTasks
                && (
                    step > (hasPendingAsks(data?.asks) ? STEP_NUM.APPLICATION_FORM_REDO : STEP_NUM.LAST_STEP)
                    || (
                        ![STEPS.APPLICATION_FORM, STEPS.DOCUMENT_VERIFICATION]?.includes(isAskForInfo?.label)
                        && [1, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)
                    )
                )
            )
        );

    const isLoading = dataLoading || !Object.keys(validationkeyValue)?.length || !CountrySelectService?.cyclosCountryMaster?.length;

    const renderApplicantHeaderActions = () => (
        <ApplicantHeaderActions
            applicationForm={applicationForm}
            formData={data}
            formId={editId}
            formType={applicationForm?.formType || FORM_TYPE.BusinessPlus}
            onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
            disabled={asLabel || editMode}
            logoutComponent={<LogoutWithReason form_id={data?._id} />}
        />
    );

    const renderInFormLayout = (content, showReviewWizard = false) => (
        <div className={!asLabel && !editMode ? 'form-content-container xl' : ""}>
            <div className='form-content-header'>
                {(!asLabel && !editMode) && <img
                    src={Logo}
                    alt={("fv-bank")}
                    className="logo logo-dark form-content-logo"
                />}
                {renderApplicantHeaderActions()}
            </div>
            {isLoading ?
                <div className=" form-content-body  d-flex justify-content-center align-items-center flex-1">
                    <FaSpinner size={20} className="spinner" />
                </div> :
                <Form className={asLabel || editMode ? "" : 'form-content-body '} noValidate {...FORM_AUTOFILL_PROPS}>
                    {(!asLabel && !editMode) && <div className='form-content-body-header'>
                        <div className="heading">
                            {data?.interestedInPrime ? ("Business Prime Account Application") : ("Business Plus Account Application")}
                            <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
                        </div>
                    </div>}

                    <div className='form-content-body-content'>
                        <ReviewShellBoundary
                            reviewNavigation={reviewNavigation}
                            loading={reviewLoading}
                            error={reviewError}
                            showReviewWizard={showReviewWizard}
                        >
                            {content}
                        </ReviewShellBoundary>
                    </div>
                </Form>
            }
        </div>
    );

    if (isDocumentRedoView) {
        return renderInFormLayout(
            <ApplicationDocRedo
                hideContainerStyle
                recordId={editId}
                asLabel={asLabel}
                formId={editId}
                formType={"BUSINESS_PLUS"}
                type={"Business Plus"}
            />,
            false
        );
    }

    if (isAskMoreMessageView) {
        return renderInFormLayout(
            <AskMoreMessage
                hideContainerStyle
                title="Business Plus"
                data={data}
                remark={(data?.remark ?? "")}
                loading={nextActionLoading}
                onNext={() => { updateStep(STEP_NUM.APPLICATION_FORM_REDO) }}
            />,
            false
        );
    }

    if (isExtraQuestionsView) {
        return renderInFormLayout(
            <ExtraQuestions
                questionIds={data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []}
                data={data}
                formType={"BUSINESS_PLUS"}
                formId={editId}
                asLabel={asLabel}
                dataLoading={nextActionLoading || dataLoading}
                handleValidation={handleValidation}
                onBack={() => {
                }}
                onSubmit={() => {
                    fetchDetail(STEP_NUM.THANK_YOU)
                }}
            />,
            false
        );
    }

    if (isThankYouView) {
        return <ThankYou
            application_id={editId}
            applicationForm={applicationForm}
            formData={data}
            type={FORM_TYPE.BusinessPlus}
        />;
    }

    function handleValidation(field, value) {
        return validateBusinessPlusField({
            field,
            value,
            data,
            fieldMap: validationkeyValue,
        });
    }

    const handleUpgradeToPrime = async () => {
        setLoading(true);
        try {
            const primaryUser = data?.individuals?.find((i) => i?.isPrimaryUser);
            const payload = {
                firstName: primaryUser?.firstName ?? "",
                middleName: primaryUser?.middleName,
                lastName: primaryUser?.lastName ?? "",
                applicantType: "Authorized_Representative",
                primaryBusiness: data?.Primary_Business_Activities, // in prime min 20 chars
                primaryCustomer: data?.Customer_Description, // in prime min 20 chars
                businessInvolvements: ["None_of_the_above"],
                Deposit_Locations: data?.Deposit_Locations ?? [],
                Payment_Locations: data?.Payment_Locations ?? [],
                isMoneyServiceBusiness: data?.Money_Services_Business,
                primaryMsbBusinessModel: data?.primaryBusinessModel, // in prime min 20 chars,
                dealsInCryptocurrency: data?.crypto,
                requiresLicense: data?.Regulated_Disclosure,
                holdsLicense: data?.Regulated_Disclosure,
                licenseDetails: data?.Regulatory_Agency?.filter(Boolean)?.join(", "),
            }
            const response = await doPOST("/api/business-prime/create", payload);
            if (response.status === 200) {
                navigate(`/business-prime/${response?.data?._id}`, { replace: true });
            }
        } catch (error) {
            showError("Error creating/retrieving BP application:", error);
            setLoading(false)
        } finally {
            setLoading(false)
        }

    };
    return (
        <div className={!asLabel && !editMode ? 'form-content-container xl' : ""}>
            <div className='form-content-header'>
                {(!asLabel && !editMode) && <img
                    src={Logo}
                    alt={("fv-bank")}
                    className="logo logo-dark form-content-logo"
                />}
                {/* {!asLabel && <div className=''><LanguagePicker /></div>} */}
                {renderApplicantHeaderActions()}
            </div>
            {isLoading ?
                <div className=" form-content-body  d-flex justify-content-center align-items-center flex-1">
                    <FaSpinner size={20} className="spinner" />
                </div> :
                <Form className={asLabel || editMode ? "" : 'form-content-body '} noValidate {...FORM_AUTOFILL_PROPS}>
                    {(!asLabel && !editMode) && <div className='form-content-body-header'>
                        <div className="heading">
                            {data?.interestedInPrime ? ("Business Prime Account Application") : ("Business Plus Account Application")}
                            <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
                        </div>
                    </div>}

                    <div className='form-content-body-content'>
                        <ReviewShellBoundary
                            reviewNavigation={reviewNavigation}
                            loading={reviewLoading}
                            error={reviewError}
                            showReviewWizard={shouldShowReviewWizard}
                        >
                            <Stepper
                                variant='dark'
                                activeTab={step}
                                asLabel={asLabel || editMode}
                                onChange={(s) => {
                                    updateStep(s);
                                }}
                            >
                                <div num={STEP_NUM.INTRODUCTION} label={("Introduction and Account Features")}>
                                    <Page1
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        hideButtons={asLabel || editMode}
                                        data={data}
                                        handleChange={handleChange}
                                        hasError={hasError || editMode}
                                        loading={nextActionLoading}
                                        onNextClick={() => updateStep(STEP_NUM.BUSINESS_DETAILS)}
                                        step={step}
                                        onBackClick={decrementStep}
                                        handleValidation={handleValidation}
                                    />
                                </div>

                                <div num={STEP_NUM.BUSINESS_DETAILS} label={"Business Information"}>
                                    <Page2
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        data={data}
                                        handleChange={handleChange}
                                        setCentralErrors={setCentralErrors}
                                        centralErrors={centralErrors}
                                        handleChangeViaObject={handleChangeViaObject}
                                        hasError={hasError || editMode}
                                        setData={setData}
                                        handleValidation={handleValidation}
                                        validationkeyValue={validationkeyValue}
                                        editMode={editMode}
                                    />

                                    <div className='mt-4'>
                                        <ButtonGroupNew
                                            onNextClick={() => handleSubmit(STEP_NUM.BUSINESS_ACTIVITY)}
                                            onBackClick={decrementStep}
                                            showPageNum
                                            pageNum={STEP_NUM.BUSINESS_DETAILS}
                                            isDisabled={asLabel || editMode}
                                            asLabel={asLabel || editMode}
                                            loading={nextActionLoading}
                                            step={step}
                                        />
                                    </div>

                                </div>

                                <div num={STEP_NUM.BUSINESS_ACTIVITY} label={"Business Activities and Account Usage"}>
                                    <Page3
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        data={data}
                                        handleChange={handleChange}
                                        handleChangeObject={setData}
                                        hasError={hasError || editMode}
                                        handleValidation={handleValidation}
                                        validationkeyValue={validationkeyValue}
                                        skipExpectedActivity={Boolean(data?.interestedInPrime)}
                                    />

                                    <div className='mt-4'>
                                        <ButtonGroupNew
                                            onNextClick={() => handleSubmit(STEP_NUM.BUSINESS_COMPLIANCE)}
                                            onBackClick={decrementStep}
                                            showPageNum
                                            pageNum={STEP_NUM.BUSINESS_ACTIVITY}
                                            isDisabled={asLabel || editMode}
                                            asLabel={asLabel || editMode}
                                            loading={nextActionLoading}
                                            step={step}
                                        />
                                    </div>

                                </div>

                                <div num={STEP_NUM.BUSINESS_COMPLIANCE} label={"Business Compliance and Licensing"}>
                                    <Page4
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        data={data}
                                        handleChangeObject={setData}
                                        hasError={hasError || editMode}
                                        validationkeyValue={validationkeyValue}
                                    />
                                    <div className='mt-4'>
                                        <ButtonGroupNew
                                            onNextClick={() => handleSubmit(STEP_NUM.DOCUMENTS)}
                                            onBackClick={decrementStep}
                                            showPageNum
                                            pageNum={STEP_NUM.BUSINESS_COMPLIANCE}
                                            isDisabled={asLabel || editMode}
                                            asLabel={asLabel || editMode}
                                            loading={nextActionLoading}
                                            step={step}
                                        />
                                    </div>
                                </div>

                                {(!asLabel && !editMode) &&
                                    <div num={STEP_NUM.DOCUMENTS} label={"Document Upload Center"}>
                                        <Page5
                                            isAskForInfo={false}
                                            remark={"Remark Here"}
                                            recordId={edit_id}
                                            asLabel={asLabel}
                                            setHasError={setHasError}
                                            data={data}
                                            handleValidation={handleValidation}
                                            validationkeyValue={validationkeyValue}
                                            loading={nextActionLoading}
                                            onNextClick={() => handleSubmit(STEP_NUM.SELF_DECLARATION)}
                                            step={step}
                                            onBackClick={decrementStep}
                                        />
                                    </div>
                                }

                                <div num={STEP_NUM.SELF_DECLARATION} label={"Signature"}>
                                    <Agreements
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        data={data}
                                        handleChange={handleChange}
                                        handleChangeObject={setData}
                                        hasError={hasError || editMode}
                                        handleValidation={handleValidation}
                                        validationkeyValue={validationkeyValue}
                                        loading={nextActionLoading}
                                    />

                                    <div className='mt-4'>
                                        <ButtonGroupNew
                                            onNextClick={() => handleSubmit(STEP_NUM.REFERRAL_AND_SOURCE)}
                                            onBackClick={decrementStep}
                                            showPageNum
                                            pageNum={STEP_NUM.SELF_DECLARATION}
                                            isDisabled={asLabel || editMode}
                                            asLabel={asLabel || editMode}
                                            loading={nextActionLoading}
                                            step={step}
                                        />
                                    </div>

                                </div>


                                <div num={STEP_NUM.REFERRAL_AND_SOURCE} label={"Referral and Source Information"}>
                                    <Page6
                                        asLabel={(asLabel || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)) && !editMode}
                                        data={data}
                                        handleChange={handleChange}
                                        handleChangeObject={setData}
                                        hasError={hasError || editMode}
                                        handleValidation={handleValidation}
                                        validationkeyValue={validationkeyValue}
                                        form_id={edit_id}
                                    />

                                    <div className='mt-4'>
                                        <ButtonGroupNew
                                            onNextClick={async () => {
                                                if (handleNext(STEP_NUM.REFERRAL_AND_SOURCE)) {
                                                    const carts = await getCarts(edit_id);
                                                    const shouldSubmit = data?.interestedInPrime && !carts?.unPaidCarts?.length
                                                    const response = await handleUpdate({
                                                        nextStep: STEP_NUM.REFERRAL_AND_SOURCE,
                                                        isLastStep: shouldSubmit ? true : false,
                                                        isfetchDetail: false
                                                    });
                                                    if (response?.status == 200) {
                                                        if (data?.interestedInPrime) {
                                                            handleUpgradeToPrime();
                                                        } else {
                                                            navigate(`/business-plus/cart/${edit_id}?goto=/business-plus/${edit_id}?step=${STEP_NUM.REFERRAL_AND_SOURCE}`, { replace: true });
                                                        }
                                                    }
                                                }
                                            }}
                                            onBackClick={decrementStep}
                                            showPageNum
                                            pageNum={STEP_NUM.REFERRAL_AND_SOURCE}
                                            isDisabled={asLabel || editMode}
                                            asLabel={asLabel || editMode}
                                            loading={nextActionLoading}
                                            step={step}

                                        />
                                    </div>
                                </div>


                                {!asLabel && !data?.interestedInPrime ? <div num={STEP_NUM.PAY_NOW} label={("Pay Now")}>
                                    {/* <PayNow
                                    data={{ ...data, formType: "BUSINESS_PLUS" }}
                                    handleUpdateFormData={async (payload) => {
                                        const response = await handleUpdate({ nextStep: STEP_NUM.PAY_NOW, payload });
                                        return response;
                                    }}
                                    setHasError={setHasError}
                                    onBackClick={() => updateStep(STEP_NUM.DOCUMENT_VERIFICATION)}
                                    onSubmit={async () => {
                                        await handleUpdate({
                                            nextStep: STEP_NUM.THANK_YOU,
                                            isfetchDetail: true,
                                            isLastStep: true,
                                            skipUpdate: true
                                        });
                                    }}
                                /> */}

                                </div> : <div></div>}


                                {
                                    (((hasPendingAsks(data?.asks)
                                        && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
                                        && applicationSteps[STEPS.APPLICATION_FORM]?.status == 2))
                                        || (asLabel && hasActiveAsks(data?.asks))
                                    )
                                    && <div num={STEP_NUM.APPLICATION_FORM_REDO} label={"Extra Information"}>
                                        <ExtraQuestions
                                            questionIds={data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []}
                                            data={data}
                                            formType={"BUSINESS_PLUS"}
                                            formId={editId}
                                            asLabel={asLabel}
                                            dataLoading={nextActionLoading || dataLoading}
                                            handleValidation={handleValidation}
                                            onBack={() => {
                                            }}
                                            onSubmit={() => {
                                                fetchDetail(STEP_NUM.THANK_YOU)
                                            }}
                                        />
                                    </div>
                                }
                            </Stepper>
                        </ReviewShellBoundary>
                    </div>
                </Form>
            }
        </div>
    );
}

export default observer(ApplyBusinessLite);
